import { AclScope } from 'libs.react';
import { LUMOS_PERMISSIONS } from '../types/permissions.types';
/**
 * VIEW+DOWNLOAD follow the access matrix (legacy download deny-list was auditor-only).
 * CRC-only download in the story AC is an intentional divergence, not an omission.
 */
const AXON_SITE_USER_PERMISSIONS = Object.values(LUMOS_PERMISSIONS).filter((permission) => ![LUMOS_PERMISSIONS.SITE_USERS_TRAINING_REPORTS].includes(permission));
export const SITE_USER_PERMISSION_MAP = {
    [AclScope.CORTEX_ROLE_AXON_SITE_USER]: AXON_SITE_USER_PERMISSIONS,
    /**
     * Shared with the CRC rather than restated, because the platform makes the investigator's
     * access a superset of it by construction: `c_axon_site_investigator` is declared with
     * `include: ['c_axon_site_user']`, and Cortex expands that chain when evaluating an ACL, so
     * a PI's effective role set is both roles. Every eConsent ACL in play lists
     * `role.c_axon_site_user` and therefore admits the PI — void_document, invite,
     * resend_invite, complete_pts_document, get_signer_app_link, and the
     * `ec__can_sign_document` property.
     *
     * This previously excluded void, on the reading that the ACL named four roles and none of
     * them was the investigator. That reading was wrong: it did not expand the inheritance.
     * `econsent-enviroment/__tests__/ECO-168` exercises each capability as a
     * `c_axon_site_investigator` and asserts it succeeds (ECO-T218, ECO-T222, ECO-T224,
     * ECO-T226), so the platform's own evidence contradicted the exclusion.
     *
     * Clinically this is also the intended model: the PI is the accountable signatory for
     * consent at the site and the CRC administers the process on their behalf, so a document
     * capability the CRC holds and the PI does not would be backwards.
     */
    [AclScope.CORTEX_ROLE_AXON_SITE_INVESTIGATOR]: AXON_SITE_USER_PERMISSIONS,
    // The CRA is blinded in Axon (AXONCONFIG-5280): `c_email`, `c_username` and `c_mobile` are withheld, and
    // `c_search` / `c_username` cannot be filtered on, hence no PARTICIPANTS_SEARCH_BY_IDENTIFIER (SSP-4636).
    [AclScope.CORTEX_ROLE_AXON_SITE_MONITOR]: [
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_VISITS,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_ACTIVITIES,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_CAREGIVERS,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_CONSENTS,
        LUMOS_PERMISSIONS.SITE_USERS_TRAINING_REPORTS,
        // View-only: download is omitted on purpose. Signed-document read is granted
        // on the Cortex object ACL so this permission is live for a provisioned org.
        LUMOS_PERMISSIONS.PARTICIPANTS_VIEW_CONSENT_PDF,
    ],
    [AclScope.CORTEX_ROLE_AXON_SITE_AUDITOR]: [
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST,
        // Unblinded on the site_subjects route and holds `c_search` / `c_username` read, so it keeps the email search.
        LUMOS_PERMISSIONS.PARTICIPANTS_SEARCH_BY_IDENTIFIER,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_VISITS,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_ACTIVITIES,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_CAREGIVERS,
        // May view a signed ICF; must not download or print it.
        LUMOS_PERMISSIONS.PARTICIPANTS_VIEW_CONSENT_PDF,
    ],
    // Read-only oversight role; omitting the write permissions is what enforces that. Blinded like the CRA
    // (AXONCONFIG-5264), so no PARTICIPANTS_SEARCH_BY_IDENTIFIER either.
    [AclScope.CORTEX_ROLE_AXON_SITE_REVIEWER]: [
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_VISITS,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_ACTIVITIES,
        LUMOS_PERMISSIONS.PARTICIPANTS_LIST_CONSENTS,
        // May view a signed ICF; must not download or print it.
        LUMOS_PERMISSIONS.PARTICIPANTS_VIEW_CONSENT_PDF,
    ],
    // Administrator is a full document principal in Studio (document author was
    // consolidated into this role). It carries void plus view and download of the
    // signed ICF PDF; other site_app surfaces still do not model this role.
    // Download here is the same access-matrix grant as PI, not the CRC-only story AC.
    [AclScope.CORTEX_ROLE_ADMINISTRATOR]: [
        LUMOS_PERMISSIONS.PARTICIPANTS_VOID_CONSENT,
        LUMOS_PERMISSIONS.PARTICIPANTS_VIEW_CONSENT_PDF,
        LUMOS_PERMISSIONS.PARTICIPANTS_DOWNLOAD_CONSENT_PDF,
        // Unblinded on the site_subjects route and not subject to the c_public_user property ACLs, so an
        // administrator reaching the participant list keeps the email / username search (SSP-4636, FRS 5).
        LUMOS_PERMISSIONS.PARTICIPANTS_SEARCH_BY_IDENTIFIER,
    ],
};
